import React, {useMemo} from "react"
import {useSirenDeliveryAttempts, AttemptMethodStatsCycle, AlertState} from "../hooks/useSirenAlertWS"
import {Card, deliveryMethodLabel, CycleTabSelector, SirenAlert} from "./AlertStatusShared"

export function DeliveryAttemptsCard({
    alertID,
    alert,
    state,
    selectedCycle,
    onCycleChange,
}: {
    alertID: string
    alert: SirenAlert | null
    state: AlertState | null
    selectedCycle: number | "all"
    onCycleChange: (c: number | "all") => void
}) {
    const {data} = useSirenDeliveryAttempts(alertID)

    const allCycles = useMemo(() => {
        if (!data) return []
        return [...new Set(data.attempts.map(a => a.cycle))].sort((a, b) => a - b)
    }, [data])

    // Aggregate for the selected cycle
    const aggregated = useMemo(() => {
        if (!data) return []
        const rows: AttemptMethodStatsCycle[] = selectedCycle === "all"
            ? data.attempts
            : data.attempts.filter(a => a.cycle === selectedCycle)

        const byMethod: Record<string, {sent: number; pendingCallback: number; failed: number; skipped: number}> = {}
        for (const r of rows) {
            const existing = byMethod[r.method] ?? {sent: 0, pendingCallback: 0, failed: 0, skipped: 0}
            byMethod[r.method] = {
                sent:            existing.sent + r.sent,
                pendingCallback: existing.pendingCallback + r.pendingCallback,
                failed:          existing.failed + r.failed,
                skipped:         existing.skipped + r.skipped,
            }
        }
        return Object.entries(byMethod).map(([method, stats]) => ({method, ...stats}))
    }, [data, selectedCycle])

    // Sort by configured delivery method order
    const resolvedMethods = alert?.useDefaultDeliveryMethods
        ? (state?.alertConfig?.deliveryMethods ?? [])
        : (alert?.deliveryMethods ?? [])

    const sorted = useMemo(() => {
        return [...aggregated].sort((a, b) => {
            const ai = resolvedMethods.indexOf(a.method)
            const bi = resolvedMethods.indexOf(b.method)
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
        })
    }, [aggregated, resolvedMethods])

    if (sorted.length === 0) return null

    return (
        <Card
            title="Delivery Attempts"
            headerActions={
                <CycleTabSelector cycles={allCycles} selected={selectedCycle} onChange={onCycleChange} />
            }
        >
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-grey border-b border-light-light-grey">
                            <th className="pb-2 font-medium">Method</th>
                            <th className="pb-2 font-medium text-center">Sent</th>
                            <th className="pb-2 font-medium text-center">Pending</th>
                            <th className="pb-2 font-medium text-center">Failed</th>
                            <th className="pb-2 font-medium text-center">Skipped</th>
                            <th className="pb-2 font-medium text-center">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(a => {
                            const total = a.sent + a.pendingCallback + a.failed + a.skipped
                            return (
                                <tr key={a.method} className="border-b border-light-light-grey last:border-0">
                                    <td className="py-2.5 font-medium text-dark-dark-grey">{deliveryMethodLabel(a.method)}</td>
                                    <td className="py-2.5 text-center text-green font-medium">{a.sent}</td>
                                    <td className="py-2.5 text-center text-blue font-medium">{a.pendingCallback}</td>
                                    <td className="py-2.5 text-center text-red font-medium">{a.failed}</td>
                                    <td className="py-2.5 text-center text-grey">{a.skipped}</td>
                                    <td className="py-2.5 text-center font-semibold text-dark-dark-grey">{total}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    )
}
